import { UpdatesNotepad } from "@/components/UpdatesNotepad";
import React from "react";
import { Outlet, useNavigate, Link } from "react-router-dom";
import { GroupedNav } from "@/components/nav/GroupedNav";
import { MobileTopBar, useMobileMenu } from "@/components/MobileTopBar";
import { useAuth } from "@/AuthContext";
import { LayoutDashboard, Users, Calendar, FileText, PlaneTakeoff, Bell, Wallet, LogOut, Building2, Quote, LifeBuoy, Landmark, Calculator, TrendingUp, UserCog, Thermometer, ShieldCheck, HardHat, CalendarClock, Car, Hotel, Home, PawPrint, Dumbbell, Croissant, School, BookOpen, Award, ClipboardCheck, Globe, CalendarRange, MessageCircle, FileSignature, CreditCard, Wrench, Ticket, ClipboardList, Banknote, ScanLine, KeyRound, Contact, Star, Receipt, ScrollText, GitMerge, Target, Timer, Stethoscope, Gavel, Utensils, PanelsTopLeft, Phone, Puzzle, Store, TreePine } from "lucide-react";
import { useActiveModules } from "@/hooks/useActiveModules";
import { moduleIcon } from "@/components/modules/moduleUtils";
import { LegalPendingBanner } from "@/components/legal/LegalPendingBanner";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { api } from "@/api";
import TestimonialDialog from "@/components/TestimonialDialog";
import KbisRequiredModal from "@/components/KbisRequiredModal";
import OnboardingWizard from "@/components/OnboardingWizard";
import PasswordChangeReminder from "@/components/PasswordChangeReminder";
import { PushEnableBanner } from "@/components/PushNotifications";
import { PortalIncidentBanner } from "@/components/IncidentBanner";
import { LanguageCurrencySwitcher } from "@/components/LanguageCurrencySwitcher";

const SECTOR_NAV = {
  haccp: { to: "/chef/secteur/haccp", label: "HACCP", icon: Thermometer },
  rondes: { to: "/chef/secteur/rondes", label: "Rondes", icon: ShieldCheck },
  chantiers: { to: "/chef/secteur/chantiers", label: "Chantiers", icon: HardHat },
  rdv: { to: "/chef/secteur/rdv", label: "Rendez-vous", icon: CalendarClock },
  vehicules: { to: "/chef/secteur/vehicules", label: "Véhicules", icon: Car },
  reservations: { to: "/chef/secteur/reservations", label: "Réservations", icon: Hotel },
  biens: { to: "/chef/secteur/biens", label: "Biens immo", icon: Home },
  pensionnaires: { to: "/chef/secteur/pensionnaires", label: "Pensionnaires", icon: PawPrint },
  abonnes: { to: "/chef/secteur/abonnes", label: "Abonnés", icon: Dumbbell },
  production: { to: "/chef/secteur/production", label: "Production", icon: Croissant },
};

const EDUCATION_NAV = [
  { to: "/chef/education/eleves", label: "Élèves", icon: Users },
  { to: "/chef/education/classes", label: "Classes", icon: School },
  { to: "/chef/education/presences", label: "Présences", icon: ClipboardCheck },
  { to: "/chef/education/presences-monthly", label: "Présences mois", icon: CalendarRange },
  { to: "/chef/education/notes", label: "Notes", icon: BookOpen },
  { to: "/chef/education/bulletins", label: "Bulletins", icon: Award },
  { to: "/chef/education/parents", label: "Parents", icon: MessageCircle },
];

const chefTestId = (item) => `chef-nav-${item.label.toLowerCase().replace(/[éèêë]/g, "e").replace(/[àâ]/g, "a").replace(/[ùû]/g, "u").replace(/[îï]/g, "i").replace(/[ôö]/g, "o").replace(/\s+/g, "-").replace(/&/g, "").replace(/\/+/g, "").replace(/-+/g, "-").replace(/^-|-$/g, "")}`;

const TOP_NAV = [
  { to: "/chef", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/chef/notifications", label: "Messages", icon: Bell },
];

/** Catégories du menu chef (Phase 74) — les entrées optionnelles sont injectées selon les options/secteur. */
const buildGroups = ({ pointageEnabled, cseEnabled, multiSiteEnabled, sector, education, dynModules }) => [
  { key: "equipe", label: "Équipe & temps", icon: Users, items: [
    { to: "/chef/salaries", label: "Salariés", icon: Users },
    { to: "/chef/planning", label: "Planning", icon: Calendar },
    ...(pointageEnabled ? [{ to: "/chef/pointage", label: "Pointage", icon: Timer }] : []),
    { to: "/chef/conges", label: "Congés & Absences", icon: PlaneTakeoff },
    { to: "/chef/visites-medicales", label: "Visites médicales", icon: Stethoscope },
    ...(cseEnabled ? [{ to: "/chef/cse", label: "CSE / CE", icon: UserCog }] : []),
  ] },
  { key: "paie", label: "Paie & RH", icon: Wallet, items: [
    { to: "/chef/paie", label: "Bulletins de paie", icon: Wallet },
    { to: "/chef/paie-fiscal", label: "Paie & Fiscal", icon: Landmark },
    { to: "/chef/titres-restaurant", label: "Titres-restaurant", icon: Utensils },
    { to: "/chef/simulateur", label: "Simulateur paie", icon: Calculator },
    { to: "/chef/documents", label: "Documents RH", icon: FileText },
    { to: "/chef/contrats", label: "Contrats & avenants", icon: ScrollText },
    { to: "/chef/templates-rh", label: "Modèles RH PDF", icon: FileSignature },
  ] },
  { key: "finance", label: "Finance", icon: TrendingUp, items: [
    { to: "/chef/finance", label: "Finance / CA", icon: TrendingUp },
    { to: "/chef/devis-factures", label: "Devis & Factures", icon: Receipt },
    { to: "/chef/encaissement", label: "Encaissement", icon: Banknote },
    { to: "/chef/banques", label: "Banques", icon: Landmark },
    { to: "/chef/rapprochement", label: "Rapprochement bancaire", icon: GitMerge },
  ] },
  { key: "commercial", label: "Clients & visibilité", icon: Globe, items: [
    { to: "/chef/talk", label: "PAG Talk · Téléphonie", icon: Phone },
    { to: "/chef/site", label: "Site web Pro", icon: PanelsTopLeft },
    { to: "/chef/minisite", label: "Mini-site public", icon: Globe },
    { to: "/chef/reservations", label: "Réservations & QR", icon: ScanLine },
    { to: "/chef/crm", label: "CRM · Prospects", icon: Target },
    { to: "/chef/clients", label: "Portail client", icon: Contact },
    { to: "/chef/avis", label: "Avis clients", icon: Star },
  ] },
  { key: "metier", label: "Métier & QualiParc", icon: Wrench, items: [
    ...sector,
    ...education,
    { to: "/chef/qualiparc/interventions", label: "QP · Interventions", icon: Wrench },
    { to: "/chef/qualiparc/audits", label: "QP · Audits", icon: ClipboardList },
    { to: "/chef/qualiparc/tickets", label: "QP · Tickets", icon: Ticket },
  ] },
  { key: "marketplace", label: "Marketplace", icon: Store, items: [
    ...dynModules.map((m) => ({ to: `/chef/modules/${m.key}`, label: m.name, icon: moduleIcon(m.icon) })),
    { to: "/chef/marketplace", label: "Marketplace PBM", icon: Store, end: true },
  ] },
  { key: "billetterie", label: "Billetterie & Parcs", icon: TreePine, items: [
    { to: "/chef/billetterie", label: "Billetterie & Parcs partenaires", icon: TreePine },
  ] },
  { key: "compte", label: "Entreprise & compte", icon: Building2, items: [
    { to: "/chef/profil-entreprise", label: "Profil entreprise", icon: Building2 },
    ...(multiSiteEnabled ? [{ to: "/chef/sites", label: "Multi-sites", icon: Building2 }] : []),
    { to: "/chef/facturation", label: "Facturation", icon: CreditCard },
    { to: "/chef/legal", label: "Informations légales", icon: Gavel },
    { to: "/chef/securite", label: "SecureConnect", icon: KeyRound },
    { to: "/chef/helper", label: "PAG Helper", icon: LifeBuoy },
  ] },
];

export default function ChefLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const mobile = useMobileMenu();
  const [cseEnabled, setCseEnabled] = useState(false);
  const [multiSiteEnabled, setMultiSiteEnabled] = useState(false);
  const [pointageEnabled, setPointageEnabled] = useState(false);
  const [sectorModules, setSectorModules] = useState([]);
  const [isEducation, setIsEducation] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setCseEnabled(Boolean(r.data?.entreprise?.cse_enabled));
      setMultiSiteEnabled(Boolean(r.data?.entreprise?.multi_site_enabled));
      setPointageEnabled((r.data?.entreprise?.options_active || []).includes("pointage_advanced"));
    }).catch(() => {});
    // Phase 89 — modules conditionnels issus du secteur libre ET des métiers (principal + secondaires) ; rafraîchis quand le gérant change ses métiers
    const loadSector = () => api.get("/chef/secteur/active-modules").then((r) => {
      setSectorModules(r.data?.modules || []);
      setIsEducation(Boolean(r.data?.education));
    }).catch(() => {});
    loadSector();
    window.addEventListener("pag:modules-changed", loadSector);
    return () => window.removeEventListener("pag:modules-changed", loadSector);
  }, []);

  const dynModules = useActiveModules();
  const sector = sectorModules.filter((m) => SECTOR_NAV[m]).map((m) => SECTOR_NAV[m]);
  const groups = buildGroups({ pointageEnabled, cseEnabled, multiSiteEnabled, sector, education: isEducation ? EDUCATION_NAV : [], dynModules });

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const status = user?.entreprise?.status;
  const statusCls = status === "active" ? "status-active" : status === "pending_payment" ? "status-pending" : status === "blocked" ? "status-blocked" : "status-inactive";
  const statusLabel = { active: "Actif", pending_payment: "Paiement en attente", blocked: "Bloqué", inactive: "Inactif" }[status] || status;

  return (
    <div className="dashboard-shell bg-white">
      <MobileTopBar portal="chef" label="Chef" open={mobile.open} onToggle={mobile.toggle} />
      <aside className={`${mobile.asideClass} border-r border-[#DEE2E6] bg-white flex-col`} onClick={mobile.onNavClick} data-testid="chef-sidebar">
        <div className="p-6 border-b border-[#DEE2E6] hidden lg:block">
          <Link to="/" className="flex items-center gap-3">
            <img src="/pag-logo.png" alt="PAG" className="h-9 w-9 object-contain" />
            <span className="font-display font-black text-lg">PAG</span>
            <span className="text-xs uppercase tracking-[0.25em] text-[#868E96] border-l border-[#DEE2E6] pl-3">Chef</span>
          </Link>
        </div>
        <div className="p-4 border-b border-[#DEE2E6]">
          <div className="text-xs uppercase tracking-[0.2em] text-[#868E96] font-semibold">Entreprise</div>
          <div className="mt-1 font-display font-bold text-sm truncate" data-testid="chef-entreprise-name">{user?.entreprise?.name || "—"}</div>
          <div className="font-mono text-xs text-[#495057] mt-1">{user?.entreprise?.code}</div>
          {status && (
            <div className={`status-pill mt-3 ${statusCls}`} data-testid="chef-entreprise-status">
              <span className="h-1.5 w-1.5 rounded-full bg-current" /> {statusLabel}
            </div>
          )}
        </div>
        <GroupedNav portal="chef" top={TOP_NAV} groups={groups} testId={chefTestId} />
        <div className="p-4 border-t border-[#DEE2E6]">
          <div className="text-xs uppercase tracking-[0.2em] text-[#868E96] mb-1">Connecté</div>
          <div className="text-sm font-semibold">{user?.prenom} {user?.nom}</div>
          <div className="font-mono text-xs text-[#495057] mb-3">{user?.code}</div>
          {/* v2.0.11 — Language & Currency switcher */}
          <div className="mb-3"><LanguageCurrencySwitcher /></div>
          <Button
            onClick={doLogout}
            data-testid="chef-logout-btn"
            variant="outline"
            className="w-full h-9 text-xs uppercase tracking-[0.2em] font-semibold rounded-md"
          >
            <LogOut className="h-3 w-3 mr-2" /> Déconnexion
          </Button>
          <TestimonialDialog
            trigger={
              <Button variant="ghost" data-testid="chef-testimonial-btn" className="w-full mt-2 h-9 text-xs uppercase tracking-[0.2em] font-semibold rounded-md text-[#002FA7] hover:bg-[#002FA7]/5">
                <Quote className="h-3 w-3 mr-2" /> Laisser un avis
              </Button>
            }
          />
          <div className="mt-4 pt-4 border-t border-[#DEE2E6] text-[10px] text-[#868E96] space-y-0.5" data-testid="chef-support">
            <div className="uppercase tracking-[0.2em] font-semibold mb-1">Support</div>
            <a href="mailto:contact@parcavenue.eu" className="block hover:text-[#002FA7] transition-colors truncate">contact@parcavenue.eu</a>
            <a href="tel:+33970701792" className="block hover:text-[#002FA7] transition-colors font-mono">09 70 70 17 92</a>
          </div>
        </div>
      </aside>

      <main className="overflow-y-auto bg-[#F8F9FA]">
        <PasswordChangeReminder />
        <PortalIncidentBanner />
        <PushEnableBanner />
        <LegalPendingBanner portal="chef" />
        <UpdatesNotepad className="mx-6 lg:mx-10 mt-4" compact />
        <Outlet />
      </main>
      <KbisRequiredModal />
      <OnboardingWizard />
    </div>
  );
}
